import { staticRoutes } from '@/api/apiRoutes';
import { Diagnostics } from '@/util/diagnostics';
import { IWeskiMapping, WeskiEntityType } from './types';

export const WESKI_MAPPINGS_TTL_MS = 12 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 5000;
const RETRY_BACKOFF_MS = 60 * 1000;

export type WeskiMappingMaps = {
  resort: Map<string, string>,
  region: Map<string, string>,
};

let cache: WeskiMappingMaps | null = null;
let cachedAt = 0;
let failedAt = 0;
let inFlight: Promise<WeskiMappingMaps> | null = null;

const fetchWithTimeout = (url: string, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
};

const buildMaps = (list: IWeskiMapping[]): WeskiMappingMaps => {
  const resort = new Map<string, string>();
  const region = new Map<string, string>();
  list.forEach((item) => {
    const target = item.entityType === 'resort' ? resort : region;
    target.set(String(item.entityId), item.weskiDestinationId);
  });
  return { resort, region };
};

const fetchWeskiMappings = async (): Promise<WeskiMappingMaps> => {
  const response = await fetchWithTimeout(staticRoutes.weskiMappings(), FETCH_TIMEOUT_MS);
  if (!response.ok) {
    throw new Error(`Failed to fetch weski mappings: ${response.status}`);
  }
  const data: IWeskiMapping[] = await response.json();
  return buildMaps(data);
};

const fetchAndCache = async (): Promise<WeskiMappingMaps> => {
  try {
    const maps = await fetchWeskiMappings();
    cache = maps;
    cachedAt = Date.now();
    failedAt = 0;
    return maps;
  } catch (error) {
    Diagnostics.error({ tag: 'weski', message: 'Failed to fetch weski mappings', error: error as Error });
    failedAt = Date.now();
    if (cache) {
      return cache;
    }
    throw error;
  } finally {
    inFlight = null;
  }
};

const refresh = (): Promise<WeskiMappingMaps> => {
  if (!inFlight) {
    inFlight = fetchAndCache();
  }
  return inFlight;
};

/**
 * Stale-while-revalidate: a cold load (no cache yet) awaits the fetch, since
 * there is nothing else to serve. Once cached, an expired entry is still
 * returned immediately while the refresh happens in the background, so a
 * slow or hanging WeSki API never adds latency to a page that already has
 * data. A failed refresh backs off for RETRY_BACKOFF_MS before trying again.
 */
export const getWeskiMappings = async (): Promise<WeskiMappingMaps> => {
  if (!cache) {
    return refresh();
  }

  const isFresh = (Date.now() - cachedAt) < WESKI_MAPPINGS_TTL_MS;
  const backedOff = !!failedAt && (Date.now() - failedAt) < RETRY_BACKOFF_MS;
  if (!isFresh && !backedOff) {
    refresh().catch(() => undefined);
  }
  return cache;
};

/**
 * Warms the in-process cache without throwing - callers that just want the
 * cache populated (root layout, server data fetchers) should use this.
 */
export const warmWeskiMappings = async (): Promise<void> => {
  await getWeskiMappings().catch(() => undefined);
};

/**
 * Synchronous read of whatever is currently cached. Returns null before the
 * first successful fetch has resolved.
 */
export const getWeskiMappingsSync = (): WeskiMappingMaps | null => cache;

export type WeskiMappingSeed = {
  resort: Array<[string, string]>,
  region: Array<[string, string]>,
  seededAt: number,
};

/**
 * Serializable snapshot of the current cache, handed to the client Providers
 * boundary by the root layout so the client's first render (hydration) sees
 * the same data the server used - the client bundle's module-level cache
 * starts empty and would otherwise cause a hydration mismatch on the very
 * first paint.
 */
export const getWeskiMappingsSeed = (): WeskiMappingSeed | null => {
  if (!cache) {
    return null;
  }
  return {
    resort: Array.from(cache.resort.entries()),
    region: Array.from(cache.region.entries()),
    seededAt: cachedAt,
  };
};

/**
 * Seeds the cache from a snapshot produced by getWeskiMappingsSeed(). Only
 * applies the seed if it is newer than whatever is already cached, so a
 * fresher SSR fetch (e.g. after the client's own cache went stale) always
 * wins, while a client that already refreshed on its own isn't regressed by
 * an older seed from a page that rendered earlier.
 */
export const seedWeskiMappings = (seed: WeskiMappingSeed): void => {
  if (cache && cachedAt >= seed.seededAt) {
    return;
  }
  cache = {
    resort: new Map(seed.resort),
    region: new Map(seed.region),
  };
  cachedAt = seed.seededAt;
};

/**
 * Direct, uncached point lookup for a single resort/region. Intended for the
 * Edge middleware, which is compiled into its own isolated bundle - module
 * state there doesn't reliably persist between requests, so the shared
 * full-list TTL cache above never actually stays warm there. Fetching just
 * one small record per lookup, only when actually needed, is cheaper than
 * re-fetching the full 530-record list on every request.
 */
export const fetchWeskiDestinationIdFor = async (
  entityType: WeskiEntityType,
  entityId: string,
): Promise<string | null> => {
  try {
    const response = await fetchWithTimeout(
      staticRoutes.weskiMappings({ entityType, entityId }),
      FETCH_TIMEOUT_MS,
    );
    if (!response.ok) {
      return null;
    }
    const data: IWeskiMapping[] = await response.json();
    return data[0]?.weskiDestinationId ?? null;
  } catch (error) {
    Diagnostics.error({ tag: 'weski', message: 'Failed to fetch weski mapping for entity', error: error as Error });
    return null;
  }
};
